<script setup lang="ts">
/**
 * Leesmodus van een veld (VInput: `display-only-value`). Toont de waarde als tekst in
 * hetzelfde grid als een bewerkbaar veld, zodat een scherm kan wisselen tussen
 * bekijken en bewerken zonder dat de layout verspringt.
 */
import { computed, ref } from 'vue'
import { formatEuNumber, formatPrice } from './formatters'
import type { DisplayType, FieldLayout } from './types'
import { displayLayoutClasses } from './useFormField'

const props = withDefaults(
    defineProps<{
        value?: unknown
        type?: DisplayType
        layout?: FieldLayout
        /** Rechts uitlijnen, bv. bedragen in een tabelrij. */
        alignRight?: boolean
    }>(),
    {
        type: 'text',
        layout: 'horizontal',
        alignRight: false,
    }
)

const rootRef = ref<HTMLElement | null>(null)
defineExpose({ rootRef })

const classes = computed<string[]>(() => [
    `${displayLayoutClasses(props.layout)} text-sm`,
    props.alignRight ? 'justify-end' : '',
])
</script>

<template>
    <div ref="rootRef" :class="classes">
        <template v-if="type === 'text' || type === 'number'">
            {{ value }}<slot name="suffix"></slot>
        </template>
        <template v-else-if="type === 'eu-number'">
            {{ formatEuNumber(value) }}<slot name="suffix"></slot>
        </template>
        <template v-else-if="type === 'price'">
            {{ formatPrice(value) }}
        </template>
        <template v-else-if="type === 'percentage'"> {{ value }}&percnt; </template>
        <template v-else-if="type === 'surface'"> {{ value }}m² </template>
        <template v-else-if="type === 'time'"> {{ value }}{{ $t('global.hour_short') }} </template>
    </div>
</template>
